import type { ApiClient } from './api-client';
import { bodyFor } from './call-shapes';
import { REMOTE_ENDPOINTS, type RemoteEndpoint, type RemoteEndpointKey } from './endpoint-catalog';

/**
 * Os casos de uso, do outro lado da rede.
 *
 * Cada um vira um objeto com um `execute` que monta o corpo e faz a requisição. A tela não
 * percebe a troca: ela continua chamando `container.getProject.execute(projectId, session)`
 * exatamente como chamava quando isso rodava no navegador. É o que a arquitetura da Fase 1
 * comprou — a interface fala com casos de uso, e nunca soube quem os implementa.
 *
 * **Não há um arquivo por endpoint, e não deve haver.** Cento e cinco funções escritas à
 * mão seriam cento e cinco chances de errar um nome de campo, e nenhuma delas diria nada
 * que a tabela já não diga. O que varia entre elas é o caminho e a forma; os dois estão no
 * catálogo, e o catálogo é conferido contra o servidor por teste.
 */

/** `CreateProject` → `/api/create-project`. Gêmeo do `pathFor` do servidor. */
function caminhoDe(nome: string): string {
  return '/api/' + nome.replaceAll(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Um caso de uso remoto tem `execute`, e é só isso que a tela usa.
 *
 * O tipo é frouxo aqui e apertado na costura (`remote-container.ts`): o objeto literal
 * abaixo não teria como satisfazer estruturalmente uma classe com campo privado, e forçar
 * isso só produziria uma acrobacia de tipos que não protege ninguém. Quem garante que as
 * chaves e as formas batem é o `endpoint-catalog.test.ts`, que compara com o servidor de
 * verdade — e isso um tipo não teria como fazer.
 */
export interface RemoteUseCase {
  execute(...args: readonly unknown[]): Promise<unknown>;
}

export type RemoteUseCases = Readonly<Record<RemoteEndpointKey, RemoteUseCase>>;

export function createRemoteUseCases(client: ApiClient): RemoteUseCases {
  // O catálogo é `as const`, então cada entrada tem um tipo próprio e estreito — bom
  // para quem escreve nele, ruim para quem o percorre: `raw` deixa de existir nas
  // entradas que não o declaram. Ler pela forma larga é o que permite a travessia.
  const entradas = (Object.entries(REMOTE_ENDPOINTS) as [string, RemoteEndpoint][]).map(
    ([chave, endpoint]) => {
      const caminho = caminhoDe(endpoint.name);
      const useCase: RemoteUseCase = {
        execute: async (...args) => {
          const resposta = await client.post(caminho, bodyFor(endpoint.shape, args));
          // O remoto precisa devolver **o que o local devolveria**, e não o que o
          // transporte devolve. Para 101 dos 106 é a mesma coisa: eles já falam em
          // `Result`. Os cinco catálogos falam em valor cru, e para eles o embrulho é
          // desfeito aqui — do contrário a tela receberia `{ ok, value }` onde espera uma
          // lista, e quebraria longe daqui, num componente que não fez nada de errado.
          if (endpoint.raw !== true) return resposta;
          // A falha vira exceção porque não há outro canal: a assinatura local não tem
          // como recusar. É o mesmo que aconteceria se o banco caísse no uso local, e o
          // `useLive` já trata — mostra o carregamento em vez de congelar dado velho.
          if (!resposta.ok) throw resposta.error;
          return resposta.value;
        },
      };
      return [chave, useCase] as const;
    },
  );

  return Object.fromEntries(entradas) as RemoteUseCases;
}
